<template>
  <section class="admin-content" id="assessments-page">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white flex-wrap" style="padding: 32px 0; gap: 12px;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3"><i class="fe fe-clipboard" style="font-size: 22px;"></i></div>
              <div>
                <h4 class="mb-0 font-weight-bold">Facility Assessments</h4>
                <small style="opacity: .8;">{{ subtitle }}</small>
              </div>
            </div>
            <router-link v-if="status && status.can_manage_links" to="/assessments/links" class="btn btn-outline-light btn-sm">
              <i class="fe fe-link mr-1"></i>hefrias link
            </router-link>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div v-if="loadingStatus" class="card shadow-sm p-4 text-muted">Loading…</div>

          <template v-else-if="status">
            <div v-if="!status.configured" class="alert alert-warning">
              Assessments are not available yet: the connection to hefrias has not been set up.
            </div>
            <div v-else-if="!status.agency_linked" class="alert alert-warning d-flex align-items-center flex-wrap" style="gap: 10px;">
              <span>Your agency is not linked to hefrias yet, so there are no forms to fill.</span>
              <router-link v-if="status.can_manage_links" to="/assessments/links" class="btn btn-sm btn-warning ml-auto">Link it now</router-link>
              <span v-else class="ml-auto small">Ask an agency admin to link it.</span>
            </div>

            <template v-else>
              <!-- Forms (facility users) -->
              <div v-if="status.can_submit" class="mb-4">
                <h6 class="section-title">Forms you can fill</h6>
                <div v-if="formsError" class="alert alert-danger small">{{ formsError }}</div>
                <div class="row">
                  <div v-for="f in forms" :key="f.id" class="col-md-6 col-lg-4 mb-3">
                    <div class="card shadow-sm form-tile h-100">
                      <div class="card-body d-flex flex-column">
                        <div class="small text-uppercase text-muted font-weight-bold">{{ f.form_type }}</div>
                        <h6 class="font-weight-bold mt-1">{{ f.title }}</h6>
                        <p class="small text-muted flex-fill mb-2">{{ f.description || `${f.items_count} questions` }}</p>
                        <router-link :to="`/assessments/forms/${f.id}`" class="btn btn-sm btn-submit align-self-start">
                          <i class="fe fe-edit-3 mr-1"></i>Fill in
                        </router-link>
                      </div>
                    </div>
                  </div>
                  <div v-if="!forms.length && !formsError && !loadingForms" class="col-12 text-muted small">
                    Your agency has no forms for facilities to fill at the moment.
                  </div>
                </div>
              </div>

              <!-- Submissions -->
              <div class="card shadow-sm table-card">
                <div class="d-flex align-items-center flex-wrap px-4 pt-4 pb-3" style="gap: 8px;">
                  <h6 class="mb-0 font-weight-bold">{{ status.can_submit ? "Your facility's assessments" : "Assessments of your facilities" }}</h6>
                  <span class="small text-muted">Filled here or by inspectors in hefrias</span>
                  <span class="badge badge-soft-info ml-auto">{{ total }}</span>
                </div>
                <div v-if="listError" class="alert alert-danger small mx-4">{{ listError }}</div>
                <div class="table-responsive">
                  <table class="table align-td-middle mb-0">
                    <thead style="background:#f8fafc;">
                      <tr>
                        <th class="table-th">Date</th>
                        <th class="table-th">Form</th>
                        <th class="table-th" v-if="!status.can_submit">Facility</th>
                        <th class="table-th">By</th>
                        <th class="table-th">Score</th>
                        <th class="table-th">Gaps</th>
                        <th class="table-th">Estimated cost</th>
                        <th class="table-th"><span class="sr-only">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="s in rows" :key="s.id" class="table-row">
                        <td class="px-4 py-3 text-nowrap">{{ (s.assessment_date || s.created_at || "").slice(0, 10) }}</td>
                        <td class="py-3">{{ s.form && s.form.title }}</td>
                        <td class="py-3" v-if="!status.can_submit">{{ s.facility_name }}</td>
                        <td class="py-3">
                          {{ s.submitted_by || "—" }}
                          <div><span class="source-badge" :class="'source-badge--' + s.source">{{ s.source === "hefrias" ? "Inspector (hefrias)" : "Claimify" }}</span></div>
                        </td>
                        <td class="py-3">
                          <span class="score" :class="scoreClass(s.metrics && s.metrics.percentile)">{{ s.metrics ? s.metrics.percentile + "%" : "—" }}</span>
                        </td>
                        <td class="py-3">{{ s.gap_totals ? s.gap_totals.count : 0 }}</td>
                        <td class="py-3 text-nowrap">
                          <template v-if="s.gap_totals && s.gap_totals.low_cost">{{ money(s.gap_totals.low_cost) }} – {{ money(s.gap_totals.high_cost) }}</template>
                          <template v-else>—</template>
                        </td>
                        <td class="py-3 pr-4 text-right">
                          <router-link :to="`/assessments/submissions/${s.id}`" class="btn btn-sm btn-view">View</router-link>
                        </td>
                      </tr>
                      <tr v-if="!rows.length && !loading">
                        <td :colspan="status.can_submit ? 7 : 8" class="text-center py-5 text-muted">
                          <i class="fe fe-clipboard" style="font-size:2rem;opacity:.3"></i>
                          <p class="mt-2 mb-0">No assessments yet.</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="d-flex justify-content-end align-items-center px-4 py-3" style="gap: 8px;" v-if="lastPage > 1">
                  <button type="button" class="btn btn-sm btn-light" :disabled="page <= 1" @click="load(page - 1)">Previous</button>
                  <span class="small">Page {{ page }} of {{ lastPage }}</span>
                  <button type="button" class="btn btn-sm btn-light" :disabled="page >= lastPage" @click="load(page + 1)">Next</button>
                </div>
              </div>
            </template>
          </template>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import { API, money, errorText, scoreClass } from "./assessments";

export default {
  components: { Navbar },
  data() {
    return {
      status: null,
      loadingStatus: true,
      forms: [],
      formsError: "",
      loadingForms: false,
      rows: [],
      total: 0,
      page: 1,
      lastPage: 1,
      loading: false,
      listError: "",
    };
  },
  computed: {
    subtitle() {
      if (this.status && this.status.can_submit) return "Fill your agency's M&E forms; see the gaps and where to get what is missing";
      return "M&E and accreditation results of your facilities, with costed gaps";
    },
  },
  created() {
    this.axios
      .get(`${API}/status`)
      .then((res) => {
        this.status = res.data;
        if (res.data.configured && res.data.agency_linked) {
          if (res.data.can_submit) this.loadForms();
          this.load(1);
        }
      })
      .catch(() => (this.status = { configured: false }))
      .finally(() => (this.loadingStatus = false));
  },
  methods: {
    money,
    scoreClass,
    loadForms() {
      this.loadingForms = true;
      this.axios
        .get(`${API}/forms`)
        .then((res) => (this.forms = res.data.data || []))
        .catch((err) => (this.formsError = errorText(err, "Could not load the forms.")))
        .finally(() => (this.loadingForms = false));
    },
    load(page) {
      this.loading = true;
      this.listError = "";
      this.axios
        .get(`${API}/submissions`, { params: { page } })
        .then((res) => {
          this.rows = res.data.data || [];
          this.total = res.data.total || 0;
          this.page = res.data.current_page || 1;
          this.lastPage = res.data.last_page || 1;
        })
        .catch((err) => (this.listError = errorText(err, "Could not load the assessments.")))
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style scoped>
.page-header { background: linear-gradient(135deg, #115e59 0%, #0d9488 100%); }
.header-icon-wrap { width: 48px; height: 48px; background: rgba(255,255,255,0.15); border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.section-title { font-weight: 700; color: #134e4a; margin: 8px 0 12px; }
.form-tile { border: none; border-radius: 12px; border-top: 3px solid #0d9488; }
.table-card { border-radius: 12px; border: none; }
.table-th { font-size: .73rem; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 600; border-bottom: 2px solid #e2e8f0; padding: 12px 16px; }
.table-row:hover { background: #f8fafc; }
.source-badge { display: inline-block; font-size: .68rem; font-weight: 600; border-radius: 10px; padding: 1px 8px; margin-top: 2px; }
.source-badge--hefrias { background: #e0e7ff; color: #3730a3; }
.source-badge--claimify { background: #ccfbf1; color: #115e59; }
.score { font-weight: 700; }
.score--good { color: #047857; }
.score--fair { color: #b45309; }
.score--poor { color: #b91c1c; }
.btn-submit { background: linear-gradient(135deg, #115e59, #0d9488); border: none; color: #fff; border-radius: 8px; font-weight: 600; }
.btn-submit:hover { opacity: .9; color: #fff; }
.btn-view { border-radius: 6px; border: 1px solid #e2e8f0; color: #115e59; background: #fff; font-size: .8rem; }
</style>
